import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, ArrowLeft, ShieldCheck, Scale, Lightbulb, TrendingUp } from "lucide-react";
import { Reveal, SectionLabel, StatBlock } from "../components/primitives";

const STRATEGIES = [
  {
    tag: "Strategy 01",
    title: "Private Equity · Growth Capital",
    summary: "We back founders building the companies that will shape India's industrial decade — from first customer pilots to institutional scale.",
    bullets: ["Seed → Series A cheques", "Concentrated, conviction-led portfolio", "Active governance and board support"],
    image: "https://images.unsplash.com/photo-1643798924495-770ed9b56853?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Industrial wind turbine close-up",
  },
  {
    tag: "Strategy 02",
    title: "Energy Transition",
    summary: "Capital for the businesses rewiring how India generates, stores and uses energy — practical technology with a clear path to scale.",
    bullets: ["Solar, storage and green hydrogen", "Waste-heat recovery and carbon capture", "Next-generation power electronics"],
    image: "https://images.unsplash.com/photo-1743352476740-0f9fcb6f939d?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Solar panels and modern architecture",
  },
  {
    tag: "Strategy 03",
    title: "Industrial & Manufacturing",
    summary: "Investing in the factories, materials and supply-chain platforms that make India globally competitive and cost-efficient.",
    bullets: ["Advanced manufacturing and robotics", "Next-gen materials and coatings", "Data centres and energy-heavy infrastructure"],
    image: "https://images.unsplash.com/photo-1765810542186-c6becc0687b1?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Industrial plant at sunset",
  },
  {
    tag: "Strategy 04",
    title: "Mobility & Electrification",
    summary: "The full mobility stack — from batteries and drivetrains to retrofit platforms and charging networks — priced for real Indian conditions.",
    bullets: ["EV powertrain and battery systems", "Retrofit and aftermarket platforms", "Charging and V2G infrastructure"],
    image: "https://images.unsplash.com/photo-1594446545939-a34b083e9a7c?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Electric vehicle charging close-up",
  },
];

const INSIGHTS = [
  {
    tag: "Outlook",
    date: "Q4 · 2025",
    title: "India 2030: the $200B industrial opportunity hiding in plain sight.",
    excerpt: "Why the next decade belongs to companies building physical infrastructure, not software — and how to invest in it.",
  },
  {
    tag: "Perspective",
    date: "Q4 · 2025",
    title: "From services to systems: India's quiet shift into deep-tech manufacturing.",
    excerpt: "Costs, policy and talent are converging. We map where the returns will land.",
  },
  {
    tag: "Thesis",
    date: "Q3 · 2025",
    title: "ESG as a competitive advantage, not a compliance checkbox.",
    excerpt: "How sustainable positioning lowers cost of capital and opens premium markets for Indian industrial companies.",
  },
];

const COMMITMENTS = [
  { icon: ShieldCheck, title: "Institutional discipline", body: "SEBI-registered, independently audited, and governed to global institutional standards." },
  { icon: Scale, title: "Alignment with investors", body: "Transparent fees, honest reporting, and a GP commitment that puts our own capital next to yours." },
  { icon: TrendingUp, title: "Long-term compounding", body: "We invest for fundamental value — real businesses, real cash flows, real infrastructure." },
  { icon: Lightbulb, title: "Measurable impact", body: "Every company is underwritten on financial return, ESG alpha, and contribution to India's transition." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* ───────────────────── HERO ───────────────────── */}
      <section className="relative overflow-hidden grain pt-20 pb-24 lg:pt-28 lg:pb-28">
        <div className="absolute inset-0 -z-0">
          <img
            src="https://images.unsplash.com/photo-1643798924495-770ed9b56853?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt=""
            className="w-full h-full object-cover opacity-[0.10]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#F9F9F7]/85 via-[#F9F9F7]/95 to-[#F9F9F7]"></div>
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 z-10">
          <Reveal>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-[10px] uppercase tracking-[0.24em] text-[#4A5E51] mb-8">
              <span className="w-2 h-2 rounded-full bg-[#C44900] ff-pulse"></span>
              <span>ESG Arbitrage</span><span className="text-[#C44900]">·</span>
              <span>Private Equity</span><span className="text-[#C44900]">·</span>
              <span>Alternatives</span><span className="text-[#C44900]">·</span>
              <span>Public Equities</span><span className="text-[#C44900]">·</span>
              <span>Fixed Income</span><span className="text-[#C44900]">·</span>
              <span>AIF Cat II</span><span className="text-[#C44900]">·</span>
              <span>SEBI Registered</span>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <Reveal delay={80}>
                <h1 className="font-display font-bold tracking-[-0.04em] leading-[0.9] text-[#111A15] text-[56px] sm:text-7xl lg:text-[112px]">
                  Values<br />
                  <span className="italic font-medium">creating</span><br />
                  <span className="inline-flex items-baseline">value<span className="text-[#C44900]">.</span></span>
                </h1>
              </Reveal>
              <Reveal delay={150}>
                <p className="mt-10 max-w-xl text-[17px] sm:text-lg leading-relaxed text-[#4A5E51]">
                  Folks Funds is <span className="text-[#111A15] font-medium">India's first ESG-led fund</span> with a global perspective. We deploy long-term capital across private equity, public markets and fixed income — backing the businesses building India's industrial and sustainable future.
                </p>
                <div className="flex flex-wrap items-center gap-3 mt-8">
                  <Link to="/philosophy" data-testid="hero-cta-thesis" className="inline-flex items-center gap-2 bg-[#1A2F24] text-white px-6 py-3.5 text-[14px] font-medium hover:bg-[#111A15] transition-colors">
                    Our approach <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/ecosystem" data-testid="hero-cta-ecosystem" className="inline-flex items-center gap-2 border border-[#1A2F24] text-[#1A2F24] px-6 py-3.5 text-[14px] font-medium hover:bg-[#1A2F24] hover:text-white transition-colors">
                    How we work
                  </Link>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-5">
              <HeroWorldMap />
            </div>
          </div>
        </div>

        {/* Stats band */}
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 mt-24 lg:mt-32 z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            <Reveal delay={50}><StatBlock value="₹500 Cr" label="Target fund size" hint="AIF Category II · SEBI" /></Reveal>
            <Reveal delay={120}><StatBlock value="₹100 Cr" label="First close" hint="Completed" /></Reveal>
            <Reveal delay={190}><StatBlock value="13" label="Investment themes" hint="Across the industrial economy" /></Reveal>
            <Reveal delay={260}><StatBlock value="$200B+" label="India opportunity" hint="Industrial transition by 2030" /></Reveal>
          </div>
        </div>
      </section>

      {/* ───────────── MARQUEE ───────────── */}
      <section className="border-y border-[#E5E5E0] py-5 overflow-hidden bg-[#F9F9F7]">
        <div className="flex whitespace-nowrap ff-marquee-track gap-12 font-display font-medium tracking-tight text-[#111A15] text-2xl sm:text-3xl">
          {Array.from({ length: 2 }).flatMap((_, i) =>
            ["Private Equity", "Growth Capital", "Energy Transition", "Industrial Manufacturing", "Mobility & Infrastructure", "Sustainable Investing"].map((t, j) => (
              <span key={`${i}-${j}`} className="flex items-center gap-12">
                <span>{t}</span>
                <span className="text-[#C44900]">●</span>
              </span>
            ))
          )}
        </div>
      </section>

      {/* ───────────── WHO WE ARE ───────────── */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionLabel>About the firm</SectionLabel>
            <h2 className="font-display font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#111A15] leading-[1.02]">
              A new kind of<br />asset manager<span className="text-[#C44900]">.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 space-y-6 text-[16px] text-[#4A5E51] leading-relaxed">
            <p>We are a private equity and alternative asset management firm focused on India — but we don't just write cheques. We build alongside the founders we back.</p>
            <p>Through our platform — <span className="text-[#111A15] font-medium">Folks Motor</span> (distribution), <span className="text-[#111A15] font-medium">Folks Finance</span> (working capital) and <span className="text-[#111A15] font-medium">Folks Funds</span> (equity) — portfolio companies get customers, capital and capability from day one.</p>
            <p>The goal is simple: help serious builders in India's industrial economy grow faster, more responsibly, and at institutional scale.</p>
            <div className="pt-4">
              <Link to="/about" className="inline-flex items-center gap-2 text-[14px] font-medium text-[#1A2F24] hover:text-[#C44900] transition-colors group">
                Learn more about the firm <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── STRATEGIES CAROUSEL ───────────── */}
      <StrategiesCarousel />

      {/* ───────────── ECOSYSTEM ───────────── */}
      <section className="py-24 lg:py-32 bg-[#E8ECE9]/40 border-y border-[#E5E5E0]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <SectionLabel>Portfolio platform</SectionLabel>
              <h2 className="font-display font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#111A15] leading-[1.02]">
                More than capital.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-[16px] text-[#4A5E51] leading-relaxed">
                We give every portfolio company access to real customers, working-capital financing and institutional equity — three things that usually take founders years to assemble.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E5E5E0] border border-[#E5E5E0]">
            {[
              { logo: "/brand/folks-motor.png", logoClass: "h-14", kicker: "01 · Distribution", title: "Folks Motor", body: "A nationwide dealer and distributor network that becomes the first customer and go-to-market channel for portfolio hardware companies.", testid: "ecosystem-motor", href: "https://www.folksmotor.com/" },
              { logo: "/brand/folks-finance.png", logoClass: "h-10", kicker: "02 · Working capital", title: "Folks Finance", body: "An NBFC offering bill discounting, distributor financing and project loans — so founders can grow without diluting equity.", testid: "ecosystem-finance" },
              { logo: "/brand/folks-funds.png", logoClass: "h-10", kicker: "03 · Equity capital", title: "Folks Funds", body: "SEBI-registered AIF Category II. Long-term equity for seed to Series A, with active governance and exit planning.", testid: "ecosystem-funds" },
            ].map((c, i) => {
              const Inner = (
                <div data-testid={c.testid} className="bg-white p-8 lg:p-10 h-full hover:bg-[#F9F9F7] transition-colors group flex flex-col">
                  <div className="h-16 mb-10 flex items-center">
                    <img src={c.logo} alt={c.title} className={`${c.logoClass} w-auto object-contain mix-blend-multiply`} />
                  </div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C44900] mb-3">{c.kicker}</p>
                  <h3 className="font-display font-semibold tracking-tight text-3xl text-[#111A15] mb-5 flex items-center gap-2">
                    {c.title}
                    {c.href && <ArrowUpRight className="w-4 h-4 text-[#4A5E51] group-hover:text-[#C44900] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />}
                  </h3>
                  <p className="text-[14px] leading-relaxed text-[#4A5E51]">{c.body}</p>
                </div>
              );
              return (
                <Reveal key={c.title} delay={i * 90}>
                  {c.href ? (
                    <a href={c.href} target="_blank" rel="noopener noreferrer" data-testid={`${c.testid}-link`} className="block h-full">{Inner}</a>
                  ) : Inner}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───────────── INDIA FOCUS ───────────── */}
      <section className="py-24 lg:py-32 bg-[#111A15] text-white relative overflow-hidden">
        <IndiaDotGrid />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 relative z-10">
          <div className="lg:col-span-7">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#9DB0A3] mb-5">— Where we invest</p>
            <h2 className="font-display font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mb-10">
              India-focused. Global in standards.
            </h2>
            <p className="text-[16px] text-[#9DB0A3] leading-relaxed max-w-xl">
              India is shifting from a services economy to a deep-tech manufacturing powerhouse. Policy tailwinds, cost advantage and a leapfrog dynamic are converging into one of the largest capital-deployment opportunities of the next decade.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col justify-between gap-8">
            <div className="grid grid-cols-2 gap-x-8 gap-y-8">
              {[
                ["$200B+", "Energy transition by 2030"],
                ["13", "Investment themes"],
              ].map(([v, l], i) => (
                <Reveal key={l} delay={i * 70}>
                  <div className="border-t border-white/20 pt-4">
                    <div className="font-display font-bold tracking-tighter text-3xl sm:text-4xl text-white">{v}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#9DB0A3] mt-2">{l}</div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={180}>
              <div className="border-t border-white/20 pt-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C44900] mb-3">— Investment thesis</p>
                <p className="font-display font-medium tracking-tight text-2xl sm:text-3xl text-white leading-[1.2]">
                  Backing scalable technology-led industrial businesses with a clear path to deployment.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ───────────── INSIGHTS ───────────── */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <SectionLabel>Insights</SectionLabel>
              <h2 className="font-display font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#111A15] leading-[1.02]">
                Perspectives from our team.
              </h2>
            </div>
            <Link to="/philosophy" className="inline-flex items-center gap-2 text-[13px] font-mono uppercase tracking-[0.18em] text-[#C44900] hover:underline">
              All perspectives <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E5E5E0] border border-[#E5E5E0]">
            {INSIGHTS.map((a, i) => (
              <Reveal key={a.title} delay={i * 90}>
                <article data-testid={`insight-${i}`} className="bg-white p-8 lg:p-10 h-full flex flex-col group hover:bg-[#F9F9F7] transition-colors">
                  <div className="flex items-center justify-between mb-12 font-mono text-[10px] uppercase tracking-[0.22em]">
                    <span className="text-[#C44900]">— {a.tag}</span>
                    <span className="text-[#4A5E51]">{a.date}</span>
                  </div>
                  <h3 className="font-display font-semibold tracking-tight text-2xl lg:text-3xl text-[#111A15] leading-[1.1] mb-6">{a.title}</h3>
                  <p className="text-[14px] text-[#4A5E51] leading-relaxed">{a.excerpt}</p>
                  <div className="mt-auto pt-8 flex items-center gap-2 text-[13px] font-medium text-[#1A2F24] group-hover:text-[#C44900] transition-colors">
                    Read more <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── COMMITMENTS ───────────── */}
      <section className="py-24 lg:py-32 bg-[#E8ECE9]/40 border-y border-[#E5E5E0]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14">
            <div className="lg:col-span-5">
              <SectionLabel>Our commitments</SectionLabel>
              <h2 className="font-display font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#111A15] leading-[1.02]">
                What we stand for.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-[16px] text-[#4A5E51] leading-relaxed">
                Four principles govern everything we do — how we select investments, how we work with founders, and how we report back to our investors.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E5E0] border border-[#E5E5E0]">
            {COMMITMENTS.map((c, i) => (
              <Reveal key={c.title} delay={i * 80}>
                <div className="bg-white p-8 h-full">
                  <c.icon className="w-7 h-7 text-[#1A2F24]" strokeWidth={1.4} />
                  <h3 className="font-display font-semibold tracking-tight text-xl text-[#111A15] mt-10 mb-3">{c.title}</h3>
                  <p className="text-[14px] text-[#4A5E51] leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── CTA ───────────── */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="bg-[#111A15] text-white p-10 lg:p-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-8">
                <SectionLabel className="text-[#9DB0A3] before:!hidden">— Partner with Folks Funds</SectionLabel>
                <h3 className="font-display font-medium tracking-tight text-3xl sm:text-5xl lg:text-6xl leading-[1.02]">
                  Long-term capital. Long-term partners. Let's talk.
                </h3>
              </div>
              <div className="lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
                <Link to="/contact" data-testid="footer-cta-contact" className="inline-flex items-center gap-2 bg-[#C44900] text-white px-6 py-3.5 hover:bg-[#A33D00] transition-colors text-sm font-medium">
                  Talk to us <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/team" data-testid="footer-cta-team" className="inline-flex items-center gap-2 border border-white/30 text-white px-6 py-3.5 hover:bg-white/10 transition-colors text-sm font-medium">
                  Meet the team
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

/* ─────────────────────────────────────────────────── */
/*  Strategies Carousel                                */
/* ─────────────────────────────────────────────────── */
function StrategiesCarousel() {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const total = STRATEGIES.length;
  const timerRef = useRef(null);

  const go = (n, d = 1) => {
    setDir(d);
    setIndex(((n % total) + total) % total);
  };
  const next = () => go(index + 1, 1);
  const prev = () => go(index - 1, -1);

  useEffect(() => {
    timerRef.current = setTimeout(() => go(index + 1, 1), 7000);
    return () => clearTimeout(timerRef.current);
  }, [index]);

  const s = STRATEGIES[index];

  return (
    <section data-testid="strategies-carousel" className="py-24 lg:py-32 bg-[#F9F9F7]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <SectionLabel>Investment strategies</SectionLabel>
            <h2 className="font-display font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#111A15] leading-[1.02] max-w-3xl">
              Where we put capital to work.
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#4A5E51]">
              {String(index + 1).padStart(2, "0")} <span className="text-[#9DB0A3]">/ {String(total).padStart(2, "0")}</span>
            </span>
            <button
              data-testid="carousel-prev"
              onClick={prev}
              aria-label="Previous strategy"
              className="w-11 h-11 border border-[#E5E5E0] grid place-items-center text-[#1A2F24] hover:bg-[#1A2F24] hover:text-white hover:border-[#1A2F24] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              data-testid="carousel-next"
              onClick={next}
              aria-label="Next strategy"
              className="w-11 h-11 border border-[#E5E5E0] grid place-items-center text-[#1A2F24] hover:bg-[#1A2F24] hover:text-white hover:border-[#1A2F24] transition-colors"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="relative bg-white border border-[#E5E5E0] overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[440px] lg:min-h-[520px]">
            {/* IMAGE */}
            <div className="lg:col-span-6 relative overflow-hidden bg-[#111A15] aspect-[4/3] lg:aspect-auto">
              <AnimatePresence initial={false} custom={dir} mode="popLayout">
                <motion.img
                  key={s.image}
                  src={s.image}
                  alt={s.alt}
                  loading="lazy"
                  initial={{ opacity: 0, scale: 1.06 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-br from-[#111A15]/10 via-transparent to-[#111A15]/60 pointer-events-none"></div>
              <span className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.22em] text-white/90">— {s.tag}</span>
            </div>

            {/* COPY */}
            <div className="lg:col-span-6 p-8 lg:p-14 flex flex-col">
              <AnimatePresence initial={false} mode="wait">
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -14 }}
                  transition={{ duration: 0.45 }}
                  className="flex-1 flex flex-col"
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C44900] mb-5">— {s.tag}</p>
                  <h3 className="font-display font-semibold tracking-tight text-3xl lg:text-4xl text-[#111A15] leading-[1.05] mb-6">{s.title}</h3>
                  <p className="text-[16px] text-[#4A5E51] leading-relaxed max-w-lg">{s.summary}</p>
                  <ul className="mt-8 border-t border-[#E5E5E0] pt-6 space-y-3">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-[14px] text-[#4A5E51]">
                        <span className="text-[#C44900] mt-1">●</span> {b}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto pt-10">
                    <Link to="/philosophy" className="inline-flex items-center gap-2 text-[14px] font-medium text-[#1A2F24] hover:text-[#C44900] transition-colors">
                      Explore the thesis <ArrowUpRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* progress dots */}
          <div className="absolute top-4 right-4 lg:top-5 lg:right-5 flex gap-1.5">
            {STRATEGIES.map((_, i) => (
              <button
                key={i}
                data-testid={`carousel-dot-${i}`}
                onClick={() => go(i, i > index ? 1 : -1)}
                aria-label={`Go to strategy ${i + 1}`}
                className={`h-1 transition-all ${i === index ? "w-8 bg-[#C44900]" : "w-4 bg-[#E5E5E0] hover:bg-[#9DB0A3]"}`}
              />
            ))}
          </div>
        </div>

        {/* strategy rail */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E5E0] border border-t-0 border-[#E5E5E0]">
          {STRATEGIES.map((st, i) => (
            <button
              key={st.title}
              onClick={() => go(i, i > index ? 1 : -1)}
              data-testid={`carousel-tab-${i}`}
              className={`bg-white p-5 text-left group transition-colors ${i === index ? "bg-[#111A15] text-white" : "hover:bg-[#F9F9F7]"}`}
            >
              <span className={`font-mono text-[10px] uppercase tracking-[0.22em] ${i === index ? "text-[#C44900]" : "text-[#C44900]"}`}>— {String(i + 1).padStart(2, "0")}</span>
              <p className={`font-display font-medium text-[15px] sm:text-base mt-2 leading-snug tracking-tight ${i === index ? "text-white" : "text-[#111A15]"}`}>{st.title}</p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────── */
/*  Hero World Map — Global Perspective                */
/* ─────────────────────────────────────────────────── */
const REGIONS = [
  { x: 24, y: 36, name: "North America" },
  { x: 33, y: 66, name: "South America" },
  { x: 50, y: 31, name: "Europe" },
  { x: 53, y: 64, name: "Africa" },
  { x: 59, y: 44, name: "Middle East" },
  { x: 71, y: 50, name: "India", highlight: true },
  { x: 80, y: 36, name: "East Asia" },
  { x: 78, y: 60, name: "Southeast Asia" },
  { x: 87, y: 76, name: "Australia" },
];

function HeroWorldMap() {
  return (
    <div className="relative w-full max-w-[640px] mx-auto" data-testid="hero-world-map">
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#C44900] mb-4 flex items-center gap-3">
        <span className="w-6 h-px bg-[#C44900]" /> Global perspective · India focused
      </p>
      <div className="relative w-full aspect-[950/620]">
        <img
          src="/brand/world-map.svg?v=3"
          alt="World map highlighting Folks Funds' global perspective with India as primary focus"
          className="absolute inset-0 w-full h-full object-contain select-none"
          draggable="false"
        />

        {REGIONS.map((r) => (
          <div
            key={r.name}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${r.x}%`, top: `${r.y}%` }}
          >
            {r.highlight ? (
              <span className="relative flex items-center justify-center">
                <span className="absolute w-7 h-7 rounded-full border-2 border-[#C44900]/70 ff-pulse" />
                <span className="absolute w-12 h-12 rounded-full border border-[#C44900]/35" />
                <span className="relative w-3 h-3 rounded-full bg-[#C44900] ring-4 ring-[#F9F9F7]" />
              </span>
            ) : (
              <span className="block w-2.5 h-2.5 rounded-full bg-[#1A2F24] ring-[3px] ring-[#F9F9F7]" />
            )}
            <span
              className={`absolute left-1/2 -translate-x-1/2 mt-2 whitespace-nowrap font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.18em] ${
                r.highlight ? "text-[#C44900] font-semibold" : "text-[#1A2F24]"
              }`}
            >
              {r.name}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-6 pt-5 border-t border-[#E5E5E0] flex items-center justify-between gap-4 text-[11px] font-mono uppercase tracking-[0.18em] text-[#4A5E51]">
        <span><span className="inline-block w-2 h-2 rounded-full bg-[#1A2F24] mr-2 align-middle" /> LP markets</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-[#C44900] mr-2 align-middle" /> Investment focus</span>
      </div>
    </div>
  );
}
function IndiaDotGrid() {
  return (
    <svg
      aria-hidden="true"
      className="absolute right-0 top-0 h-full w-full lg:w-2/3 opacity-[0.18] pointer-events-none"
      viewBox="0 0 600 500"
      preserveAspectRatio="xMaxYMid slice"
    >
      <defs>
        <pattern id="dotGrid" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="1" fill="#9DB0A3" />
        </pattern>
        <radialGradient id="fade" cx="70%" cy="50%" r="60%">
          <stop offset="0%" stopColor="white" stopOpacity="1" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </radialGradient>
        <mask id="fadeMask">
          <rect width="600" height="500" fill="url(#fade)" />
        </mask>
      </defs>
      <rect width="600" height="500" fill="url(#dotGrid)" mask="url(#fadeMask)" />
      {[
        [420, 140, "DEL"],
        [380, 260, "BOM"],
        [470, 340, "BLR"],
        [430, 230, "PUN"],
        [510, 370, "CHE"],
        [500, 270, "HYD"],
        [340, 200, "AMD"],
      ].map(([x, y, c]) => (
        <g key={c}>
          <circle cx={x} cy={y} r="4" fill="#C44900" />
          <circle cx={x} cy={y} r="12" fill="none" stroke="#C44900" strokeWidth="0.8" opacity="0.5" />
        </g>
      ))}
    </svg>
  );
}
